import {clients} from '@cl/shared';
import {AxiosProgressEvent, CancelToken} from 'axios';
import {getUserAuth} from 'common/src/modules/auth/auth.service';
import {PreviewFile} from '../../components/CLFilePreview/CLFilePreview';
import apiV2Service from '../api/api-v2.service';
import apiService from '../api/api.service';
import {fileUploadApi} from '../fileUpload/fileUpload.api';
import {accountProfileToClientModel} from './clients.dataConverters';
import {
  AccountProfile,
  AddClientProps,
  AdminSettings,
  AgencySettings,
  BaseClient,
  CampaignWorkflowSettings,
  Client,
  ConsumedQuotas,
  RemoteClient,
  OrganizationDetailsTabData,
  ContactTabData,
  EnabledFeaturesTabData,
  LimitsAndQuotasTabData,
} from './clients.types';
const {clientPromise} = clients;

const BASE_PATH = 'Clients';

async function getAll(): Promise<BaseClient[]> {
  const {data} = await apiV2Service.executeGetRequest<BaseClient[]>(`${BASE_PATH}/GetAll`);

  return data;
}

async function getClientById(id: number): Promise<BaseClient> {
  const {data} = await apiService.executeGetRequest<BaseClient[]>(`${BASE_PATH}/Get`, {id});

  const [client] = data;

  return client;
}

async function updateClientStatus({accountId, isActive}: {accountId: number; isActive: boolean}) {
  const {data} = await apiV2Service.executePutRequest<void>(
    `${BASE_PATH}/Account/${accountId}/UpdateStatus`,
    {
      isActive,
    },
  );

  return data;
}

async function updateClientName({
  accountId,
  accountName,
}: {
  accountId: number;
  accountName: string;
}) {
  const {data} = await apiV2Service.executePutRequest<void>(
    `${BASE_PATH}/Account/${accountId}/UpdateName`,
    {
      accountName,
    },
  );

  return data;
}

async function getAdminSettings(accountId: number): Promise<AdminSettings> {
  const {data} = await apiV2Service.executeGetRequest<AdminSettings>(
    `${BASE_PATH}/Account/${accountId}/AdminSettings`,
  );

  return data;
}

function updateOrganizationDetailsTab({
  accountId,
  data,
}: {
  accountId: number;
  data: OrganizationDetailsTabData;
}) {
  return apiV2Service.executePatchRequest<void>(
    `${BASE_PATH}/Account/${accountId}/AdminSettings/OrganizationDetails`,
    {},
    {...data},
  );
}

function updateContactTab({
  accountId,
  data,
}: {
  accountId: number;
  data: ContactTabData;
}) {
  return apiV2Service.executePatchRequest<void>(
    `${BASE_PATH}/Account/${accountId}/AdminSettings/Contact`,
    {},
    {...data},
  );
}

function updateEnabledFeaturesTab({
  accountId,
  data,
}: {
  accountId: number;
  data: EnabledFeaturesTabData;
}) {
  return apiV2Service.executePatchRequest<void>(
    `${BASE_PATH}/Account/${accountId}/AdminSettings/EnabledFeatures`,
    {},
    {...data},
  );
}

function updateLimitsAndQuotasTab({
  accountId,
  data,
}: {
  accountId: number;
  data: LimitsAndQuotasTabData;
}) {
  return apiV2Service.executePatchRequest<void>(
    `${BASE_PATH}/Account/${accountId}/AdminSettings/LimitsAndQuotas`,
    {},
    {...data},
  );
}

function updateAccountProfile({
  accountId,
  accountProfile,
  campaignWorkflowSettings,
}: {
  accountId: number;
  accountProfile: AccountProfile;
  campaignWorkflowSettings: CampaignWorkflowSettings;
}) {
  return apiV2Service.executePutRequest<void>(
    `${BASE_PATH}/AccountProfile`,
    {
      accountId,
    },
    {
      ...accountProfile,
      campaignWorkflowSettings,
    },

    {
      sendAsFormData: false,
    },
  );
}

async function getAccountProfile({
  clientId,
  cancelToken,
  shouldClearCache,
}: {
  clientId?: number;
  cancelToken?: CancelToken;
  shouldClearCache?: boolean;
}): Promise<Client> {
  const client = clientId ?? getUserAuth().clientId;

  if (!clientPromise.current || shouldClearCache) {
    clientPromise.current = apiV2Service
      .executeGetRequest<RemoteClient>(
        `${BASE_PATH}/AccountProfile`,
        {clientId: client},
        {cancelToken},
      )
      .then((res) => res.data)
      .catch((e) => {
        clientPromise.current = null as unknown as Promise<RemoteClient>;
        throw e;
      });
  }

  const newClient = await clientPromise.current;

  return accountProfileToClientModel(newClient);
}

async function getAccountLogo(clientId: number) {
  const {data} = await apiV2Service.executeGetRequest<{logoUrl: string}>(
    `${BASE_PATH}/AccountProfile/Logo`,
    {clientId},
  );

  return data.logoUrl;
}

async function getConsumedQuotas(clientId: number): Promise<ConsumedQuotas> {
  const {data} = await apiV2Service.executeGetRequest<ConsumedQuotas>(
    `${BASE_PATH}/AccountConsumedQuatas`,
    {clientId},
  );

  return data;
}

async function uploadClientLogo(
  file: PreviewFile,
  onUploadProgress: (progressEvent: AxiosProgressEvent) => void,
) {
  if (!file.file) {
    throw new Error('File is required for upload');
  }

  const {data: presignedUrl} = await apiV2Service.executeGetRequest<string>(
    `${BASE_PATH}/logo-presigned-url`,
    {
      fileKey: file.fileName,
    },
  );

  await fileUploadApi.uploadFileToS3({
    url: presignedUrl,
    file: file.file,
    contentType: file.file.type,
    onUploadProgress: onUploadProgress,
  });
  const s3File = new URL(presignedUrl);

  return s3File.origin + s3File.pathname;
}

async function addAdvertiser(params: AddClientProps): Promise<Client> {
  const {data} = await apiV2Service.executePutRequest<RemoteClient>(`${BASE_PATH}/AddAdvertiser`, {
    ...params,
  });

  return accountProfileToClientModel(data);
}

async function getAgencySettings(accountId: number): Promise<AgencySettings> {
  const {data} = await apiV2Service.executeGetRequest<AgencySettings>(
    `${BASE_PATH}/Account/${accountId}/AgencySettings`,
  );

  return data;
}

function updateAgencySettings({
  accountId,
  agencySettings,
}: {
  accountId: number;
  agencySettings: AgencySettings;
}) {
  return apiV2Service.executePatchRequest<void>(
    `${BASE_PATH}/Account/${accountId}/AgencySettings`,
    {},
    {
      ...agencySettings,
    },
  );
}

export default {
  getAll,
  updateClientStatus,
  getClientById,
  getAdminSettings,
  updateOrganizationDetailsTab,
  updateContactTab,
  updateEnabledFeaturesTab,
  updateLimitsAndQuotasTab,
  updateAccountProfile,
  getAccountProfile,
  getAccountLogo,
  getConsumedQuotas,
  uploadClientLogo,
  updateClientName,
  addAdvertiser,
  getAgencySettings,
  updateAgencySettings,
};
